import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Form } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { MODULES, ROUTES } from '@common/constants';
import { fileUpload, getSignedUrl } from '@common/utils';
import CommonHeader from '@components/CommonHeader';
import { AppContext } from '@context';

import CommonTabHeader from '../../../components/CommonTabHeader';
import history from '../../../historyData';
import OrganizationMastersTab from '../../organizations/components/OrganizationMastersTab';
import MaterialsForm from '../components/MaterialsForm';
import { CREATE_MATERIAL, UPDATE_MATERIAL } from '../graphql/Mutations';
import { GET_MATERIALS, GET_SIGNED_URL } from '../graphql/Queries';

const CreateMaterials = ({ isOrganization = false }) => {
  const { mode, id, materialId, orgId, orgMode } = useParams();
  const [form] = Form?.useForm();
  const { dispatch } = useContext(AppContext);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isView, setIsView] = useState(false);
  const [materialLogo, setMaterialLogo] = useState([]);

  const [createMaterial, { loading: createLoading }] = useMutation(
    CREATE_MATERIAL,
  );

  const [updateMaterial, { loading: updateLoading }] = useMutation(
    UPDATE_MATERIAL,
  );

  const [getMaterial, { data: materialData }] = useLazyQuery(GET_MATERIALS);

  const fetchMaterial = async () => {
    try {
      await getMaterial({
        variables: { where: { id: materialId } },
        fetchPolicy: 'network-only',
      });
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    if (mode === 'view') {
      setIsView(true);
    }
    if (materialId) {
      fetchMaterial();
    }
  }, []);

  const baseRoute = `${
    isOrganization ? `${ROUTES?.ORGANIZATIONS}/${orgMode}/${orgId}` : ''
  }${ROUTES?.MASTER}${ROUTES?.MATERIAL_CATEGORIES}`;

  const onEdit = () => {
    setIsView(false);
    history?.push(`${baseRoute}${ROUTES?.MATERIALS}/${id}/edit/${materialId}`);
  };

  const onCancelEdit = () => {
    setIsView(true);
    history?.push(`${baseRoute}/${mode}${ROUTES?.MATERIALS}/${id}`);
  };

  const onFinish = async (values) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });

    const { isActive, tooltipTitle, tooltipBody, ...rest } = values;

    const dataToSend = {
      ...rest,
      tooltip: { title: tooltipTitle, body: tooltipBody },
      isActive: JSON.parse(isActive),
    };

    try {
      let imgKey;
      if (values?.imageUrl && !materialLogo?.[0]?.url) {
        await getSignedUrl(
          values?.imageUrl?.[0]?.originFileObj,
          GET_SIGNED_URL,
        )?.then(async (res) => {
          const signedImageUrl = res?.getMaterialUploadSignedUrl?.signedUrl;
          imgKey = res?.getMaterialUploadSignedUrl?.key;
          await fileUpload(
            signedImageUrl,
            values?.imageUrl?.[0]?.originFileObj,
          );
          dataToSend.imageUrl = imgKey;
        });
      } else if (materialLogo?.[0]?.url) {
        delete dataToSend.imageUrl;
      }
      if (materialId) {
        try {
          await updateMaterial({
            variables: {
              where: { id: materialId },
              data: {
                ...dataToSend,
              },
            },
            onCompleted: () => onCancelEdit(),
          });
        } catch (error) {
          return error;
        }
      } else {
        try {
          await createMaterial({
            variables: {
              data: {
                ...dataToSend,
                materialCategoryId: id,
              },
            },
            onCompleted: () => onCancelEdit(),
          });
        } catch (error) {
          return error;
        }
      }
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    if (materialData?.material?.data) {
      const dataToPass = {
        key: materialData?.material?.data?.key,
        label: materialData?.material?.data?.label,
        description: materialData?.material?.data?.description,
        imageUrl: materialData?.material?.data?.imageUrl,
        isActive: JSON.stringify(materialData?.material?.data?.isActive),
        tooltipTitle: materialData?.material?.data?.tooltip?.title,
        tooltipBody: materialData?.material?.data?.tooltip?.body,
      };
      form?.setFieldsValue(dataToPass);
      setMaterialLogo([{ url: materialData?.material?.data?.imageUrl }]);
    }
  }, [materialData]);

  const handleShowPrompt = () => {
    setIsDisabled(false);
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: true,
    });
  };

  const renderHeaderRightContent = () => (
    <>
      {isView ? (
        <Button className="common-button" size="middle" onClick={onEdit}>
          Edit
        </Button>
      ) : (
        <>
          <Button
            type="primary"
            className="common-button mr-16"
            size="middle"
            onClick={onCancelEdit}
          >
            Cancel
          </Button>
          <Button
            className="common-button"
            size="middle"
            onClick={form?.submit}
            loading={createLoading || updateLoading}
            disabled={createLoading || updateLoading || isDisabled}
          >
            Save
          </Button>
        </>
      )}
    </>
  );

  const materialHeading = id ? `${mode} Material` : 'Add Material';

  const renderHeader = () => (
    <CommonTabHeader heading={materialHeading}>
      {renderHeaderRightContent()}
    </CommonTabHeader>
  );

  const materialCard = (
    <Card
      className={
        isOrganization
          ? 'full-height-without-nav-vertical-tabs'
          : 'full-height-without-nav'
      }
    >
      <MaterialsForm
        form={form}
        onFinish={onFinish}
        handleShowPrompt={handleShowPrompt}
        isView={isView}
        materialLogo={materialLogo}
        setMaterialLogo={setMaterialLogo}
      />
    </Card>
  );

  return (
    <>
      <CommonHeader
        heading={isOrganization ? MODULES?.MASTER : materialHeading}
        customLink={ROUTES?.MASTER}
      >
        {!isOrganization && <>{renderHeaderRightContent()}</>}
      </CommonHeader>
      {isOrganization ? (
        <OrganizationMastersTab>
          {renderHeader()}
          {materialCard}
        </OrganizationMastersTab>
      ) : (
        materialCard
      )}
    </>
  );
};

export default CreateMaterials;
